import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useInView } from 'react-intersection-observer';
import { Media, SnakeToCamelObject } from '@browsing/api';
import { cn, DarkModeProvider } from '@lux/core';
import { Text, Preset } from '@lux/text/tw';

import { Campaign } from '@shared/types/mylounge';
import { TimeLeft } from '@client/components/shared/timeLeft';
import { getCategoryTabId } from '@client/redux/modules/mylounge';
import { buildCampaignUrl } from '@client/helpers/campaignUrl';

import Discount from './discount';

interface CampaignCardMiniProps {
    campaign: Campaign;
    media: Omit<SnakeToCamelObject<Media>, 'path'>;
    hideDiscount?: boolean;
    hideTimeLeft?: boolean;
    bgTransparent?: boolean;
    isPlusVariant?: boolean;
    onClick?: () => void;
    onView?: () => void;
}

interface ContentWithVariantProps {
    campaign: Campaign;
    dateEnd: number;
    hideDiscount: boolean;
    hideTimeLeft: boolean;
    isPlusVariant: boolean;
}

const ContentWithVariant = ({
    campaign,
    dateEnd,
    hideDiscount,
    hideTimeLeft,
    isPlusVariant,
}: ContentWithVariantProps) => (
    <div
        className={cn(
            'pt-xs pl-xs pb-s flex flex-grow flex-col',
            isPlusVariant ? 'bg-brand-loyalty' : 'bg-transparent',
        )}
    >
        <Text
            className={cn(
                'line-clamp-2 w-full flex-1 overflow-hidden text-start font-bold',
                '[line-height:1.4]',
                isPlusVariant ? 'text-function-0' : 'text-function-100',
            )}
            data-testid="campaign-card-mini-title"
            preset={Preset.S}
        >
            {campaign.name}
        </Text>

        {/* There are multiple design variants of CampaignCardMini components,
            some without discount or without timeleft component
        */}

        {!hideDiscount && (
            <>
                <div className="h-xs" />
                <Discount campaign={campaign} isPlusVariant={isPlusVariant} cardVariant="mini" />
            </>
        )}
        {!hideTimeLeft && (
            <>
                <div className="h-s" />
                <TimeLeft dateTill={dateEnd} />
            </>
        )}
    </div>
);

const CampaignCardMini = ({
    campaign,
    media,
    hideDiscount = false,
    hideTimeLeft = false,
    bgTransparent = false,
    isPlusVariant = false,
    onClick = () => {},
    onView = () => {},
}: CampaignCardMiniProps) => {
    const { campaignId } = campaign;
    const dateEnd = useMemo(() => new Date(campaign.endsAt).getTime(), [campaign.endsAt]);
    const categoryTabId = useSelector(getCategoryTabId);
    const destination = buildCampaignUrl(campaign.campaignId, categoryTabId);

    const { ref, inView } = useInView({
        threshold: 0.5,
        triggerOnce: true,
    });

    useEffect(() => {
        if (inView && onView) {
            onView();
        }
    }, [inView, onView]);

    return (
        <a
            id={campaignId}
            href={destination}
            onClick={onClick}
            ref={ref}
            className={cn(
                'relative flex h-full w-full flex-col transition-all duration-[330ms]',
                bgTransparent ? 'bg-transparent' : 'bg-background-elevated',
            )}
        >
            <div className="relative aspect-square w-full [--overlay-gradient:linear-gradient(180deg,rgba(0,0,0,0.03)_31.74%,rgba(0,0,0,0.3)_90%)]">
                <img className="h-full w-full object-cover" src={media.url} alt={media.altText} />
                <div className="z-1 pointer-events-none absolute inset-0 bg-[var(--overlay-gradient)]" />
            </div>

            {isPlusVariant ? (
                <DarkModeProvider>
                    <ContentWithVariant
                        hideDiscount={hideDiscount}
                        hideTimeLeft={hideTimeLeft}
                        campaign={campaign}
                        dateEnd={dateEnd}
                        isPlusVariant={isPlusVariant}
                    />
                </DarkModeProvider>
            ) : (
                <ContentWithVariant
                    hideDiscount={hideDiscount}
                    hideTimeLeft={hideTimeLeft}
                    campaign={campaign}
                    dateEnd={dateEnd}
                    isPlusVariant={isPlusVariant}
                />
            )}
        </a>
    );
};

export { CampaignCardMini };
